import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Badge } from '../../components/ui/badge'
import { Input } from '../../components/ui/input'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import CopyableId from '../../components/CopyableId'
import ProfileDetail from '../profile-safe/ProfileDetail'
import { getLocalApiUrl, authFetch } from '../../config/api'
import PatronComplianceStrip from '../../components/patron/PatronComplianceStrip'
import { displayMembershipNumber, displayPlayerId, ID_LABELS } from '../../utils/patronIdentity'
import WatchlistBulkImportModal from './WatchlistBulkImportModal'

const STATUS_OPTIONS = ['open', 'investigating', 'suppressed', 'cleared']

function RiskScoreCell({ score }) {
  if (score == null || score === '') {
    return <span className="text-sm text-gray-400">—</span>
  }
  const n = Number(score)
  if (Number.isNaN(n)) {
    return <span className="text-sm text-gray-400">—</span>
  }
  let cls = 'text-sm text-gray-800 tabular-nums'
  if (n >= 90) cls = 'text-sm text-red-600 font-semibold tabular-nums'
  else if (n >= 70) cls = 'text-sm text-orange-600 font-semibold tabular-nums'
  else if (n >= 40) cls = 'text-sm text-amber-600 font-medium tabular-nums'
  return <span className={cls}>{n}</span>
}

/** Collapse M123 / 123 / MP000123 so one patron does not appear twice (backup to API DISTINCT ON). */
function watchlistPatronDedupeKey(memberId) {
  const s = String(memberId || '').trim()
  if (!s) return ''
  if (/^M\d+$/i.test(s)) {
    const n = s.slice(1).replace(/^0+/, '')
    return n === '' ? '0' : n
  }
  if (/^MP\d+$/i.test(s)) {
    const n = s.replace(/^MP/i, '').replace(/^0+/, '')
    return n === '' ? '0' : n
  }
  if (/^\d+$/.test(s)) {
    const n = s.replace(/^0+/, '')
    return n === '' ? '0' : n
  }
  return s.toLowerCase()
}

function dedupeWatchlistRowsByPatron(rows) {
  const best = new Map()
  for (const r of rows) {
    const k = watchlistPatronDedupeKey(r.memberId)
    const mapKey = k || `__empty_${r.memberId || best.size}`
    const prev = best.get(mapKey)
    if (!prev) {
      best.set(mapKey, r)
      continue
    }
    const prevLu = prev.lastUpdated ? new Date(prev.lastUpdated).getTime() : 0
    const lu = r.lastUpdated ? new Date(r.lastUpdated).getTime() : 0
    if (lu > prevLu) best.set(mapKey, r)
  }
  return [...best.values()]
}

function watchlistAddedSource(row) {
  const notes = String(row.notes || '').toLowerCase()
  const by = String(row.addedBy || '').trim().toLowerCase()
  if (by === 'system') return 'alert'
  if (notes.includes('auto-added by rule')) return 'alert'
  if (notes.includes('auto-added from alert')) return 'alert'
  if (notes.includes('rule violation')) return 'alert'
  if (notes.includes('added due to rule')) return 'alert'
  const ruleId = row.ruleId != null && String(row.ruleId).trim() !== ''
  if (ruleId && by === 'system') return 'alert'
  return 'direct'
}

function PatronThumb({ memberId, photoUrl, avatarAvailable, label }) {
  const [src, setSrc] = useState(() =>
    photoUrl && /^https?:\/\//i.test(String(photoUrl).trim()) ? String(photoUrl).trim() : null
  )

  useEffect(() => {
    if (photoUrl && /^https?:\/\//i.test(String(photoUrl).trim())) {
      setSrc(String(photoUrl).trim())
      return undefined
    }
    let blobUrl = null
    let dead = false
    if (!avatarAvailable || !memberId) {
      setSrc(null)
      return undefined
    }
    setSrc(null)
    ;(async () => {
      try {
        const res = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(memberId)}/avatar`))
        if (!res.ok || dead) return
        const blob = await res.blob()
        if (dead || !blob?.size || blob.size < 32) return
        blobUrl = URL.createObjectURL(blob)
        if (!dead) setSrc(blobUrl)
      } catch {
        if (!dead) setSrc(null)
      }
    })()
    return () => {
      dead = true
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [memberId, photoUrl, avatarAvailable])

  const initial = (label && label !== '—' ? label : memberId || '?').trim().charAt(0).toUpperCase()
  if (!src) {
    return (
      <div
        className="h-11 w-11 shrink-0 rounded-full bg-slate-200 flex items-center justify-center text-sm font-semibold text-slate-600"
        aria-hidden
      >
        {initial}
      </div>
    )
  }
  return <img src={src} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover border border-gray-200 bg-slate-50" />
}

function RecalculateAllRiskButton() {
  const [loading, setLoading] = useState(false)
  const handleClick = async (e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    try {
      setLoading(true)
      const url = getLocalApiUrl('/api/watchlist/risk/recalculate-all')
      const res = await authFetch(url, { method: 'POST' })
      const json = await res.json()
      if (!json?.success) throw new Error(json?.error || 'Failed')
      alert(`Risk recalculated for ${json.data?.processed ?? 0} watchlist member(s). Open a member to see updated scores.`)
    } catch (e) {
      alert(e?.message || 'Recalculate all risk failed')
    } finally {
      setLoading(false)
    }
  }
  return (
    <Button type="button" variant="outline" disabled={loading} onClick={handleClick}>
      {loading ? 'Recalculating…' : 'Recalculate all risk'}
    </Button>
  )
}

export default function WatchList() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [status, setStatus] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query.trim()), 350)
    return () => clearTimeout(t)
  }, [query])

  const inRange = (iso, f, t) => {
    if (!iso) return false
    const d = new Date(iso)
    if (f && d < new Date(f)) return false
    if (t) {
      const end = new Date(t)
      end.setHours(23, 59, 59, 999)
      if (d > end) return false
    }
    return true
  }

  const [apiData, setApiData] = useState([])
  const [addModal, setAddModal] = useState(false)
  const [importModal, setImportModal] = useState(false)
  const [listReloadToken, setListReloadToken] = useState(0)
  const todayIso = (() => {
    const d = new Date()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${d.getFullYear()}-${m}-${day}`
  })()
  const [newPerson, setNewPerson] = useState({
    member_id: '',
    first_name: '',
    last_name: '',
    dob: todayIso,
    document_type: 'Driver Licence',
    document_number: '',
    severity: 'low',
    status: 'open',
    priority: 'medium',
    egm_id: '',
    comments: '',
  })
  const [memberSearch, setMemberSearch] = useState({ q: '', results: [], loading: false })
  const [attachments, setAttachments] = useState([])
  const [personIdForUpload, setPersonIdForUpload] = useState(null)
  const [showProfile, setShowProfile] = useState(false)
  const [listTab, setListTab] = useState('active')

  const normalize = (row) => ({
    memberId: row.memberId || row.member_id || row.masterProfileId || row.master_profile_id || row.patron_id || '',
    masterProfileId: row.masterProfileId || row.master_profile_id || row.alert_master_profile_id || '',
    membershipNumber: row.membershipNumber || row.membership_number || row.alert_membership_number || row.member_id || '',
    playerId: row.playerId || row.player_id || '',
    status: (row.status || 'open').toLowerCase(),
    firstSeen: row.firstSeen || row.first_seen,
    lastUpdated: row.lastUpdated || row.last_updated,
    firstName: row.first_name || row.firstName || '',
    lastName: row.last_name || row.lastName || '',
    riskScore: row.risk_score != null ? row.risk_score : row.riskScore,
    riskLevel: row.risk_level || row.riskLevel || null,
    complianceStage: row.compliance_stage || row.complianceStage || 'fresh',
    cddTier: row.cdd_tier || row.cddTier || null,
    patronPhotoUrl: row.patron_photo_url || row.patronPhotoUrl || null,
    patronAvatarAvailable:
      row.patron_avatar_available === true ||
      row.patron_avatar_available === 1 ||
      row.patron_avatar_available === 't' ||
      row.patronAvatarAvailable === true,
    addedBy: row.added_by != null ? row.added_by : row.addedBy,
    ruleId: row.rule_id != null ? row.rule_id : row.ruleId,
    notes: row.notes || '',
    alertType: row.alert_type || row.alertType || '',
  })

  useEffect(() => {
    const fetchWatchlist = async () => {
      try {
        const qs = new URLSearchParams({ tab: listTab })
        if (debouncedQ) qs.set('q', debouncedQ)
        if (status) qs.set('status', status)
        const res = await authFetch(getLocalApiUrl(`/api/watchlist?${qs}`))
        if (!res.ok) throw new Error('failed')
        const data = await res.json()
        if (data.success) setApiData(dedupeWatchlistRowsByPatron((data.data || []).map(normalize)))
      } catch (e) {
        setApiData([])
      }
    }
    fetchWatchlist()
  }, [listTab, debouncedQ, status, listReloadToken])

  const source = apiData

  const rows = useMemo(() => {
    return source.filter((item) => (!from && !to) || inRange(item.firstSeen, from, to))
  }, [source, from, to])

  const stats = useMemo(() => {
    const total = rows.length
    const byStatus = STATUS_OPTIONS.reduce((a, s) => ({ ...a, [s]: rows.filter((r) => r.status === s).length }), {})
    return { total, byStatus }
  }, [rows])

  const memberDates = useMemo(() => {
    const byMember = {}
    rows.forEach((r) => {
      const mid = r.memberId || ''
      if (!byMember[mid]) byMember[mid] = { firstSeen: null, lastUpdated: null }
      const fs = r.firstSeen ? new Date(r.firstSeen).getTime() : null
      const lu = r.lastUpdated ? new Date(r.lastUpdated).getTime() : null
      if (fs && (!byMember[mid].firstSeen || fs < byMember[mid].firstSeen)) byMember[mid].firstSeen = r.firstSeen
      if (lu && (!byMember[mid].lastUpdated || lu > byMember[mid].lastUpdated)) byMember[mid].lastUpdated = r.lastUpdated
    })
    return byMember
  }, [rows])

  const displayName = (item) => {
    const fn = (item.firstName || '').trim()
    const ln = (item.lastName || '').trim()
    if (fn || ln) return `${fn} ${ln}`.trim()
    return '—'
  }

  const patronIdLine = (item) => {
    const row = {
      master_profile_id: item.masterProfileId,
      membership_number: item.membershipNumber,
      member_id: item.playerId || item.memberId,
      player_id: item.playerId,
    }
    const patronId = item.masterProfileId || null
    const memberNo = displayMembershipNumber(row)
    const playerId = displayPlayerId(row)
    return { patronId, memberNo, playerId }
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-gray-500">Patrons on watchlist</div>
            <div className="text-2xl font-semibold text-psg-teal">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-gray-500">Open / Investigating</div>
            <div className="text-lg font-semibold">
              <span className="text-red-600 mr-2">{stats.byStatus.open || 0}</span>
              <span className="text-amber-600">{stats.byStatus.investigating || 0}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-gray-500">Suppressed / Cleared</div>
            <div className="text-lg font-semibold text-gray-700">
              <span className="mr-2">{stats.byStatus.suppressed || 0}</span>
              <span className="text-green-700">{stats.byStatus.cleared || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${listTab === 'active' ? 'border-psg-teal text-psg-teal' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          onClick={() => setListTab('active')}
        >
          Active
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${listTab === 'previous' ? 'border-psg-teal text-psg-teal' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          onClick={() => setListTab('previous')}
        >
          Previous / Inactive
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="w-72"
          placeholder="Search name or member ID"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select className="h-10 border border-gray-300 rounded-md px-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All status</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">First seen from</span>
          <input type="date" className="h-10 border border-gray-300 rounded-md px-2 text-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span className="text-xs text-gray-500">to</span>
          <input type="date" className="h-10 border border-gray-300 rounded-md px-2 text-sm" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <RecalculateAllRiskButton />
        <Button variant="outline" className="ml-auto" onClick={() => setImportModal(true)}>
          Import
        </Button>
        <Button onClick={() => setAddModal(true)}>
          Add to Watchlist
        </Button>
      </div>

      <WatchlistBulkImportModal
        open={importModal}
        onClose={() => setImportModal(false)}
        onCommitted={() => {
          setListReloadToken((n) => n + 1)
        }}
      />

      <div className="overflow-x-auto border rounded-md bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide w-14">Photo</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Member</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Added</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Compliance</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">First seen</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Updated</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {rows.map((item, idx) => (
              <tr key={`${watchlistPatronDedupeKey(item.memberId)}-${idx}`}>
                <td className="px-3 py-3 align-middle">
                  <PatronThumb
                    memberId={item.memberId}
                    photoUrl={item.patronPhotoUrl}
                    avatarAvailable={item.patronAvatarAvailable}
                    label={displayName(item)}
                  />
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="font-medium text-gray-900">{displayName(item)}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                    {(() => {
                      const ids = patronIdLine(item)
                      const detailKey = ids.memberNo || item.memberId
                      return (
                        <>
                          {ids.patronId ? (
                            <button
                              type="button"
                              className="text-psg-teal hover:underline font-mono"
                              onClick={() => setShowProfile(ids.patronId)}
                            >
                              <CopyableId value={ids.patronId} label={ID_LABELS.patron} />
                            </button>
                          ) : null}
                          {ids.memberNo ? (
                            <button
                              type="button"
                              className="text-psg-teal hover:underline font-mono"
                              onClick={() => setShowProfile(item.masterProfileId || detailKey)}
                            >
                              <CopyableId value={ids.memberNo} label={ID_LABELS.membership} />
                            </button>
                          ) : null}
                          {ids.playerId && ids.playerId !== ids.memberNo ? (
                            <span className="font-mono text-gray-500" title={ID_LABELS.player}>
                              {ID_LABELS.player} {ids.playerId}
                            </span>
                          ) : null}
                          {!ids.patronId && !ids.memberNo && detailKey ? (
                            <button
                              type="button"
                              className="text-psg-teal hover:underline"
                              onClick={() => setShowProfile(item.masterProfileId || detailKey)}
                            >
                              <CopyableId value={detailKey} label={null} />
                            </button>
                          ) : null}
                        </>
                      )
                    })()}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">
                  {watchlistAddedSource(item) === 'alert' ? (
                    <div>
                      <span className="inline-flex text-xs font-medium text-violet-800 bg-violet-100 px-2 py-0.5 rounded">
                        Alert / rule
                      </span>
                      {item.ruleId ? (
                        <div className="text-xs text-gray-500 mt-1 font-mono">{item.ruleId}</div>
                      ) : null}
                    </div>
                  ) : (
                    <div>
                      <span className="inline-flex text-xs font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                        Direct
                      </span>
                      <div className="text-xs text-gray-600 mt-1">
                        {item.addedBy ? `by ${item.addedBy}` : 'added by unknown'}
                      </div>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm">
                  <PatronComplianceStrip
                    size="compact"
                    complianceStage={item.complianceStage}
                    riskScore={item.riskScore}
                    riskLevel={item.riskLevel}
                    cddTier={item.cddTier}
                  />
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {memberDates[item.memberId]?.firstSeen || item.firstSeen
                    ? new Date(memberDates[item.memberId]?.firstSeen || item.firstSeen).toLocaleString()
                    : '—'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {memberDates[item.memberId]?.lastUpdated || item.lastUpdated
                    ? new Date(memberDates[item.memberId]?.lastUpdated || item.lastUpdated).toLocaleString()
                    : '—'}
                </td>
                <td className="px-4 py-3 text-sm">
                  <Badge
                    className={
                      item.status === 'open'
                        ? 'bg-red-100 text-red-800'
                        : item.status === 'investigating'
                          ? 'bg-amber-100 text-amber-800'
                          : item.status === 'suppressed'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-green-100 text-green-800'
                    }
                  >
                    {item.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        navigate(`/monitor-safe/watch-list/${encodeURIComponent(item.memberId)}${listTab === 'previous' ? '?inactive=1' : ''}`)
                      }
                    >
                      View
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showProfile && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30" onClick={() => setShowProfile(false)} />
          <div className="w-full max-w-4xl bg-white h-full shadow-xl overflow-y-auto">
            <div className="p-2 border-b flex justify-end">
              <button type="button" className="p-2 rounded hover:bg-gray-100" onClick={() => setShowProfile(false)}>
                Close
              </button>
            </div>
            <div className="p-2">
              <ProfileDetail profileId={showProfile} onBack={() => setShowProfile(false)} />
            </div>
          </div>
        </div>
      )}

      {addModal && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black bg-opacity-30" onClick={() => setAddModal(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-4xl bg-white shadow-xl flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="font-medium">Add Person of Interest</div>
              <button type="button" onClick={() => setAddModal(false)}>
                Close
              </button>
            </div>
            <div className="flex-1 overflow-y-auto grid grid-cols-1 lg:grid-cols-3 gap-0">
              <div className="lg:col-span-2 p-4 space-y-3 text-sm">
                <input
                  className="input-field"
                  placeholder="Member # (optional)"
                  value={newPerson.member_id}
                  onChange={(e) => setNewPerson((p) => ({ ...p, member_id: e.target.value }))}
                />
                <div className="flex gap-2">
                  <input
                    className="input-field flex-1"
                    placeholder="Search member (id or name)"
                    value={memberSearch.q}
                    onChange={(e) => setMemberSearch((s) => ({ ...s, q: e.target.value }))}
                  />
                  <Button
                    type="button"
                    onClick={async () => {
                      try {
                        setMemberSearch((s) => ({ ...s, loading: true }))
                        const userStr = localStorage.getItem('paysafego_user')
                        const token = userStr ? JSON.parse(userStr).token || '' : ''
                        const r = await fetch(getLocalApiUrl(`/api/members/search?q=${encodeURIComponent(memberSearch.q)}`), {
                          headers: { Authorization: `Bearer ${token}` },
                        })
                        const j = await r.json()
                        setMemberSearch((s) => ({ ...s, results: j.data || [], loading: false }))
                      } catch {
                        setMemberSearch((s) => ({ ...s, loading: false }))
                      }
                    }}
                  >
                    Search
                  </Button>
                </div>
                {memberSearch.results.length > 0 && (
                  <div className="border rounded p-2 max-h-40 overflow-y-auto">
                    {memberSearch.results.map((m) => (
                      <div
                        key={m.member_id}
                        className="py-1 px-2 hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          const dobVal = m.date_of_birth ? String(m.date_of_birth).slice(0, 10) : newPerson.dob || ''
                          setNewPerson((p) => ({
                            ...p,
                            member_id: String(m.member_id || ''),
                            first_name: m.first_name || '',
                            last_name: m.last_name || '',
                            dob: dobVal,
                          }))
                          setMemberSearch({ q: '', results: [], loading: false })
                        }}
                      >
                        {m.member_id} — {m.first_name} {m.last_name}
                        {(m.phone || m.email) && (
                          <span className="text-xs text-gray-500 ml-1">({[m.phone, m.email].filter(Boolean).join(' • ')})</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <input
                  className="input-field"
                  placeholder="First Name"
                  value={newPerson.first_name}
                  onChange={(e) => setNewPerson((p) => ({ ...p, first_name: e.target.value }))}
                />
                <input
                  className="input-field"
                  placeholder="Last Name"
                  value={newPerson.last_name}
                  onChange={(e) => setNewPerson((p) => ({ ...p, last_name: e.target.value }))}
                />
                <input
                  type="date"
                  className="input-field"
                  placeholder="DOB"
                  value={newPerson.dob}
                  onChange={(e) => setNewPerson((p) => ({ ...p, dob: e.target.value }))}
                />
                <select className="input-field" value={newPerson.document_type} onChange={(e) => setNewPerson((p) => ({ ...p, document_type: e.target.value }))}>
                  <option>Driver Licence</option>
                  <option>Passport</option>
                  <option>ID Card</option>
                </select>
                <input
                  className="input-field"
                  placeholder="Document Number"
                  value={newPerson.document_number}
                  onChange={(e) => setNewPerson((p) => ({ ...p, document_number: e.target.value }))}
                />
                <input className="input-field" placeholder="EGM (optional)" value={newPerson.egm_id} onChange={(e) => setNewPerson((p) => ({ ...p, egm_id: e.target.value }))} />
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Attach Document (image/PDF)</label>
                  <input
                    type="file"
                    className="block w-full text-sm"
                    onChange={async (e) => {
                      const f = e.target.files?.[0]
                      if (!f) return
                      try {
                        let id = personIdForUpload
                        if (!id) {
                          const r = await authFetch(getLocalApiUrl('/api/watchlist/persons'), {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(newPerson),
                          })
                          const j = await r.json()
                          if (j.success) {
                            id = j.data.id
                            setPersonIdForUpload(id)
                          } else {
                            alert('Save person first failed')
                            return
                          }
                        }
                        const fd = new FormData()
                        fd.append('file', f)
                        const r2 = await authFetch(getLocalApiUrl(`/api/watchlist/persons/${id}/attachments`), { method: 'POST', body: fd })
                        const j2 = await r2.json()
                        if (j2.success) setAttachments(j2.data || [])
                      } catch {
                        alert('Upload failed')
                      }
                    }}
                  />
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {attachments.map((a) => (
                      <div key={a.id} className="text-xs text-gray-600">
                        {a.file_name} <span className="text-gray-400">({a.mime_type}, {a.size_bytes} bytes)</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-xs text-gray-600 mb-1">Comments</label>
                  <textarea
                    className="input-field w-full"
                    rows={6}
                    placeholder="Enter detailed notes; you can add more later"
                    value={newPerson.comments}
                    onChange={(e) => setNewPerson((p) => ({ ...p, comments: e.target.value }))}
                  />
                </div>
              </div>
              <div className="border-l p-4 space-y-3 text-sm">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Priority</label>
                  <select className="input-field" value={newPerson.priority} onChange={(e) => setNewPerson((p) => ({ ...p, priority: e.target.value }))}>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Severity</label>
                  <select className="input-field" value={newPerson.severity} onChange={(e) => setNewPerson((p) => ({ ...p, severity: e.target.value }))}>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Status</label>
                  <select className="input-field" value={newPerson.status} onChange={(e) => setNewPerson((p) => ({ ...p, status: e.target.value }))}>
                    <option value="open">Open</option>
                    <option value="investigating">Investigating</option>
                    <option value="suppressed">Suppressed</option>
                    <option value="cleared">Cleared</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setAddModal(false)}>
                Close
              </Button>
              <Button
                className="bg-psg-teal hover:bg-psg-teal-700 text-white"
                onClick={async () => {
                  if (!newPerson.first_name?.trim() && !newPerson.last_name?.trim()) {
                    alert('Please enter at least First Name or Last Name')
                    return
                  }
                  try {
                    const memberId = String(newPerson.member_id || '').trim()
                    if (!memberId) {
                      alert('Member # is required to add a patron to the watchlist')
                      return
                    }
                    const namePart = [newPerson.first_name, newPerson.last_name].filter((x) => String(x || '').trim()).join(' ').trim()
                    const noteParts = [
                      newPerson.comments?.trim(),
                      namePart ? `Name: ${namePart}` : null,
                      newPerson.dob ? `DOB: ${newPerson.dob}` : null,
                      newPerson.document_number ? `${newPerson.document_type || 'ID'}: ${newPerson.document_number}` : null,
                    ].filter(Boolean)
                    const payload = {
                      member_id: memberId,
                      alert_type: 'POI',
                      severity: newPerson.severity || 'medium',
                      status: newPerson.status || 'open',
                      notes: noteParts.join(' · ') || 'Added from Watch List',
                    }
                    const r = await authFetch(getLocalApiUrl('/api/watchlist'), {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(payload),
                    })
                    const j = await r.json()
                    if (j.success) {
                      setAddModal(false)
                      setNewPerson({
                        member_id: '',
                        first_name: '',
                        last_name: '',
                        dob: todayIso,
                        document_type: 'Driver Licence',
                        document_number: '',
                        severity: 'low',
                        status: 'open',
                        priority: 'medium',
                        egm_id: '',
                        comments: '',
                      })
                      setMemberSearch({ q: '', results: [], loading: false })
                      alert('✓ Person added to watchlist')
                      try {
                        const qs = new URLSearchParams({ tab: listTab })
                        if (debouncedQ) qs.set('q', debouncedQ)
                        if (status) qs.set('status', status)
                        const listRes = await authFetch(getLocalApiUrl(`/api/watchlist?${qs}`))
                        const listData = await listRes.json()
                        if (listData.success) setApiData(dedupeWatchlistRowsByPatron((listData.data || []).map(normalize)))
                      } catch { /* ignore */ }
                    } else {
                      alert('Failed to add: ' + (j.error || 'Unknown error'))
                    }
                  } catch (e) {
                    alert('Error: ' + (e?.message || 'Failed to add person'))
                  }
                }}
              >
                Add to Watchlist
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
